import {
  ACTION_TYPE,
  AcoFormData,
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  NormalCreative,
  VerticalVideoStrategy,
} from '@tt4b/ttam-enums';
import { computed, Ref, ref } from '@vue/composition-api';
import {
  useAdditionalCardInfoApi,
  useCreateCardApi,
  useCreateTilesApi,
  useCreativeIs,
  useStdAdgroupIs,
  useStdCreativeIs,
  useStore,
  getAttachParams,
  useStdCreativeAdgroupIs,
  useCreativeActionTypeIs,
  useCreativeAnchor,
  useStdCreativeCard,
  useCatalogStore,
  useCatalogProductNum,
  useStdCampaignIs,
  useInjectAbTest,
} from '@tt4b/ttam-hooks';
import {
  getCountryCodeId,
  getCtaKeyById,
  getIframeId,
  imgSrcPrefix,
  getImgSrcByEnv,
  getPosterUrlFromCreativeForm,
  jsonToQuery,
  logger,
  underscoreFormDataDeep,
  getAdTypeByDetail,
} from '@tt4b/ttam-utils';
import { i18n } from '@tt4b/ttam-i18n';
import { CatalogPreview } from './../../index';
import defaultAvatar from '@tt4b/ttam-assets/src/images/creative/default-avatar.png';

const { state } = useStore();
const aadvid = computed(() => state?.baseInfo?.value?.aadvid);
const lang = computed(() => state?.baseInfo?.value?.lang);

export async function useGetPreview(options: { creativeData: Ref<FullNormalFormData> }) {
  const { creativeData } = options;
  const cardId = computed(() => creativeData.value.cardId);

  const { previewInfo, previewInfoV2, type } = await useAdditionalCardInfoApi(cardId.value);
  const thumbnailUrl = getImgSrcByEnv({ URL: imgSrcPrefix.URL }, previewInfoV2 || previewInfo);
  return {
    thumbnailUrl,
    type,
  };
}

export function useGenerateCardParams(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { campaignData, adgroupData, creativeData } = options;
  const { isStandardAdsWithShop, isStandardAdsWithCatalog, isStandardAdsWithShowcase } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const { previewVerticalVideoStrategy, previewVerticalImageStrategy } = useStdCreativeIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    creativeFormData: creativeData,
  });

  const productInfo = computed(() => {
    const _productInfo = {
      productSource: adgroupData.value.productSource,
      shopId: '',
      bcId: '',
      identityId: '',
      identityType: 0,
      catalogId: adgroupData.value.productPlatformId,
      productSetId: creativeData.value.productSetId,
      productIds: creativeData.value.dpaProducts,
      spuIds: [],
      verticalVideoStrategy: previewVerticalVideoStrategy.value,
      verticalImageStrategy: previewVerticalImageStrategy.value,
    };

    if (isStandardAdsWithShop.value) {
      return {
        ..._productInfo,
        shopId: adgroupData.value.shopId,
        bcId: adgroupData.value.shopAuthorizedBc,
        spuIds: creativeData.value.spuList,
      };
    }
    if (isStandardAdsWithCatalog.value) {
      return {
        ..._productInfo,
        bcId: adgroupData.value.catalogAuthorizedBc,
        // 如果是catalog video，传入card编辑当前的预览的产品ID
        productIds:
          previewVerticalVideoStrategy.value === VerticalVideoStrategy.TPL_VIDEO &&
          creativeData.value.catalogVideoInfo?.productId
            ? [creativeData.value.catalogVideoInfo?.productId]
            : creativeData.value.dpaProducts,
      };
    }
    if (isStandardAdsWithShowcase.value) {
      return {
        ..._productInfo,
        spuIds: creativeData.value.showcaseSpuList.map(item => item.spuId),
        identityId: adgroupData.value.identityId,
        identityType: adgroupData.value.identityType,
      };
    }
    return _productInfo;
  });

  return {
    productInfo,
  };
}

export function usePropsDrawerUrl(source = 'editor') {
  const attachParams = { ...getAttachParams(), v2: '1' };
  const urlMap = {
    editor: `/creative/interactive_add_ons/add_ons${jsonToQuery(attachParams)}`,
    library: `/creative/interactive_add_ons/assets${jsonToQuery(attachParams)}`,
  };
  const url = urlMap[source];
  return {
    url,
  };
}

export function usePropsDrawerEvents() {
  return {
    events: ['init', 'success', 'close'],
  };
}

export function usePropsInitData(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  normalCreative: Ref<NormalCreative>;
  createMode: Ref<string>;
  isCreateNewAddon?: Ref<boolean>;
}) {
  const { campaignData, adgroupData, creativeData, normalCreative, createMode, isCreateNewAddon } = options;
  const { isCatalogVideo } = useCreativeIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    normalCreativeData: normalCreative,
    createMode,
  });
  const { productInfo } = useGenerateCardParams({ campaignData, adgroupData, creativeData });
  const scene = computed(() => {
    const createCode = 0;
    const copyCode = 1;
    const editCode = 2;
    const sceneCode = createCode;
    // if (isCopy.value) sceneCode = copyCode;
    // if (isEdit.value) sceneCode = editCode;

    return sceneCode;
  });
  const { title } = CatalogPreview.useGetCatalogTitle({
    adgroupData,
    creativeData,
  });
  const { approvedCount } = useCatalogProductNum({
    adgroupFormData: adgroupData,
    creativeFormData: creativeData,
  });
  const initData = computed(() => {
    const identityName = creativeData.value.source || creativeData.value.appName;
    const videoInfo = isCatalogVideo.value
      ? creativeData.value.catalogVideoInfo?.videoInfo
      : creativeData.value.videoInfo;
    const product_info = {
      ...underscoreFormDataDeep(productInfo.value),
      approved_count: approvedCount.value,
      video_template: creativeData.value.videoTplName,
      vertical_creative_strategy: creativeData.value.verticalCreativeStrategy,
    };
    const avatarUrl =
      getImgSrcByEnv(
        { URL: imgSrcPrefix.URL },
        creativeData.value.avatarIcon?.url || creativeData.value.avatarIcon?.webUri,
      ) || defaultAvatar;
    const creative_info = {
      nickname: identityName ? `@${identityName}` : '',
      cta: i18n.t(getCtaKeyById(creativeData.value.callToAction)),
      avatar: avatarUrl,
      text: isCatalogVideo.value ? title.value : creativeData.value.title,
      ad_type: getAdTypeByDetail(creativeData.value),
    };
    const platform_info = {
      objective: adgroupData.value.objectiveType,
      placement: adgroupData.value.inventoryFlow,
      video_id: creativeData.value.videoInfo?.vid,
      title: creativeData.value.title,
      scene: scene.value,
      page_id: creativeData.value.pageId,
      country_code: (getCountryCodeId(adgroupData.value.country) || []).join(','),
      ad_id: adgroupData.value.adId,
      external_type: adgroupData.value.externalType,
      source: creativeData.value.source,
      app_name: creativeData.value.appName,
      app_id: adgroupData.value.adRefAppId,
      external_url: creativeData.value.externalUrl,
      objective_type: adgroupData.value.objectiveType,
      portfolio_id: creativeData.value.cardId,
      adv_id: aadvid.value,
      is_aco: false,
      search_campaign_type: campaignData.value.searchCampaignType,
    };
    const metaInfo = creativeData.value.catalogVideoInfo?.metaInfo;
    logger.debug('Card usePropsInitData = %s', product_info);
    return {
      caller: 'ad-web-i18n',
      id: getIframeId(),
      adv_id: aadvid.value,
      lang: lang.value,
      attachment_creative_id: isCreateNewAddon.value ? '' : creativeData.value.cardId,
      meta_info: metaInfo,
      creative_info,
      platform_info,
      product_info,
      video_info: {
        ...videoInfo,
        play_url: videoInfo?.playUrl || videoInfo?.originPlayUrl,
        poster_url: getPosterUrlFromCreativeForm(creativeData.value),
      },
    };
  });

  return {
    initData,
  };
}

export function usePropsErrorTipVisible(options: { creativeData: Ref<FullNormalFormData & AcoFormData> }) {
  const { creativeData } = options;
  const cardId = computed(() => creativeData.value.cardId);
  const visible = computed(
    () => !cardId.value && creativeData.value.cardInfo?.createFail && !creativeData.value.cardInfo?.loading,
  );
  return {
    visible,
  };
}

export function usePropsTileWarningVisible(options: {
  campaignFormData: Ref<CampaignFormData>;
  adgroupFormData: Ref<AdgroupFormData>;
  creativeFormData: Ref<FullNormalFormData>;
  createMode: Ref<string>;
}) {
  const { createMode, ...otherFormData } = options;
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: otherFormData.campaignFormData });

  if (isProductSalesCloseLoop.value) {
    return {
      visible: computed(() => false),
    };
  }

  const { isProductTile, isEnoughProductNumForTile } = useStdCreativeCard(otherFormData);
  const { selectedCatalog } = useCatalogStore({ adgroupFormData: otherFormData.adgroupFormData });
  // 编辑的时候，如果数据不够4个了，那就要提示
  const visible = computed(
    () =>
      selectedCatalog.value &&
      isProductTile.value &&
      !isEnoughProductNumForTile.value &&
      createMode.value === ACTION_TYPE.EDIT,
  );
  return {
    visible,
  };
}

export async function useGenerateCardOrTile(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  createMode: Ref<string>;
}) {
  const { campaignData, adgroupData, creativeData, createMode } = options;
  const { productInfo } = useGenerateCardParams(options);

  const { isEnoughProductNumForTile, isProductTile } = useStdCreativeCard({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    creativeFormData: creativeData,
  });

  const { isEdit, isCopy } = useCreativeActionTypeIs({
    adgroupFormData: adgroupData,
    createMode,
  });

  const params = {
    creativeInfo: {
      cta: i18n.t(getCtaKeyById(creativeData.value.callToAction)).toString(),
    },
    productInfo: productInfo.value,
    cardId: creativeData.value.cardId,
  };
  let isCreateProductTile = false;
  if (isEdit.value || isCopy.value) {
    isCreateProductTile = isEnoughProductNumForTile.value && isProductTile.value;
  }

  const { id, previewInfo, previewInfoV2, type } = await (isCreateProductTile ? useCreateTilesApi : useCreateCardApi)(
    params,
  );
  const cardThumbnail = getImgSrcByEnv({ URL: imgSrcPrefix.URL }, previewInfoV2 || previewInfo);
  const cardId = id;
  const cardType = type;
  return {
    cardId,
    cardInfo: {
      thumbnailUrl: cardThumbnail
        ? `${getImgSrcByEnv({ URL: imgSrcPrefix.URL }, cardThumbnail)}?time=${Date.now()}`
        : '',
      cardType,
      loading: false,
      createFail: false,
    },
  };
}

export function usePropsEditorVisible(options: { creativeData: Ref<FullNormalFormData> }) {
  const { creativeData } = options;
  const visible = computed(() => !creativeData.value.cardInfo?.createFail && creativeData.value.cardId);

  return {
    visible,
  };
}

export function usePropsEditorDisabled(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  createMode: Ref<string>;
  normalCreative: Ref<NormalCreative>;
}) {
  const { campaignData, adgroupData, creativeData, normalCreative, createMode } = options;
  const { isSelectedProductsAvailable } = useCreativeIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    normalCreativeData: normalCreative,
    createMode,
  });
  const disabled = computed(() => !isSelectedProductsAvailable.value);

  return {
    disabled,
  };
}

export function usePropsAddonBtnVisible(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  normalCreative: Ref<NormalCreative>;
  createMode: Ref<string>;
}) {
  const {
    campaignData: campaignFormData,
    adgroupData: adgroupFormData,
    normalCreative: normalCreativeData,
    creativeData,
    createMode,
  } = options;
  const { isEdit } = useCreativeActionTypeIs({
    adgroupFormData,
    createMode,
  });
  const { isAddonCreativeAlreadyHas } = useCreativeAnchor({
    campaignFormData,
    adgroupFormData,
    normalCreativeData,
  });
  const { isStandardAds, isStandardAdsWithCatalog } = useStdCreativeAdgroupIs({
    campaignFormData,
    adgroupFormData,
  });
  const { isProductTile: isAddonEditBtnHide } = useStdCreativeCard({
    campaignFormData,
    adgroupFormData,
    creativeFormData: creativeData,
  });
  const isLoading = computed(() => creativeData.value.cardInfo?.loading);
  const { isVSAEditingCapacityExpansion } = useInjectAbTest();
  const isAddonDeleteBtnDisabled = computed(() => {
    if (isEdit.value) {
      const isEditable = isVSAEditingCapacityExpansion.value && isStandardAdsWithCatalog.value;
      return !isEditable;
    }
    return false;
  });
  const isShowAddonPreviewThumbnail = computed(
    () => isStandardAds.value && (isAddonCreativeAlreadyHas.value || isLoading.value),
  );
  return {
    isShowAddonPreviewThumbnail,
    isAddonDeleteBtnDisabled,
    isAddonEditBtnHide,
  };
}
